import { useState, useEffect } from "react";
import { fetchVehicles, createVehicle, updateVehicle } from "./utils/api";

import { vehicleToEvent } from "./utils/vehicleUtils";
import { LEGEND } from "./constants/data";

import { IconHome, IconCal, IconMenu, IconPlus } from "./components/icons/Icons";

import HomeView  from "./components/views/HomeView";
import YearView  from "./components/views/YearView";
import MonthView from "./components/views/MonthView";

import RightPanel        from "./components/panels/RightPanel";
import AddVehicleSidebar from "./components/sidebars/AddVehicleSidebar";
import SettingsSidebar   from "./components/sidebars/SettingsSidebar";
import Header            from "../components/Headers/Header";
import { useNavigate }   from "react-router-dom";
import { signOut }       from "../utils/authorizationUtils";

export default function VehicleCalendar() {
  const navigate = useNavigate();
  const username = localStorage.getItem("username") || "User";
  const [year, setYear]                       = useState(2026);
  const [month, setMonth]                     = useState(2);       // 0-based → March
  const [activeNav, setActiveNav]             = useState("year");
  const [sidebarOpen, setSidebarOpen]         = useState(false);
  const [addVehicleOpen, setAddVehicleOpen]   = useState(false);    
  const [vehicles, setVehicles]                 = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [loadingVehicles, setLoadingVehicles]   = useState(false);
  const [vehiclesError, setVehiclesError]       = useState(null);

  // Load vehicles from backend on mount
  useEffect(() => {
    setLoadingVehicles(true);
    fetchVehicles()
      .then(data => setVehicles(data))
      .catch(err => setVehiclesError(err.message))
      .finally(() => setLoadingVehicles(false));
  }, []);

  // Always derive the live vehicle object from the vehicles array
  const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId) ?? null;

  function handleYearChange(delta) {
    setYear(y => y + delta);
  }

  function handleMonthChange(delta) {
    setMonth(m => {
      const nm = m + delta;
      if (nm < 0)  { setYear(y => y - 1); return 11; }
      if (nm > 11) { setYear(y => y + 1); return 0;  }
      return nm;
    });
  }  async function handleAddVehicle(form) {
    try {
      const saved = await createVehicle(form);
      setVehicles(v => [...v, saved]);
    } catch {
      // fallback: optimistic local add
      setVehicles(v => [...v, { ...form, id: Date.now() }]);
    }
  }
  const HG_ORDER = ["HG0","HG1","HG2","HG3","HG4","HG5","HG6"];  async function handleGrantHG(vehicleId) {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return;
    const next = HG_ORDER[HG_ORDER.indexOf(vehicle.hgStatus) + 1];
    if (!next) return;

    // Update both hgStatus and haertegrad in local state immediately
    setVehicles(vs => vs.map(v =>
      v.id === vehicleId ? { ...v, hgStatus: next, haertegrad: next } : v
    ));

    // Persist to backend (toBackendFields maps hgStatus → haertegrad)
    try { await updateVehicle(vehicleId, { hgStatus: next }); } catch { /* ignore */ }
  }// Only user-added vehicle events
  const allEvents = vehicles.map(vehicleToEvent).filter(Boolean);

  // Map each unique projektname → a colour from the LEGEND palette
  const PROJECT_COLORS = LEGEND.map(l => l.color);
  const projectColorMap = {};
  let colorIdx = 0;
  vehicles.forEach(v => {
    const name = v.projektname || v.projectName;
    if (name && !projectColorMap[name]) {
      projectColorMap[name] = PROJECT_COLORS[colorIdx % PROJECT_COLORS.length];
      colorIdx++;
    }
  });

  const NAV_ITEMS = [
    { id: "home",  label: "Home",  Icon: IconHome },
    { id: "year",  label: "Year",  Icon: IconCal  },
    { id: "month", label: "Month", Icon: IconCal  },
  ];
  return (    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#f0f2f5", fontFamily: "'Segoe UI', Tahoma, Verdana, sans-serif" }}>      {/* ── Top bar ── */}
      <Header
        user={{ profile: { preferred_username: username?.includes("@") ? username.split("@")[0] : username } }}
        signOut={signOut}
        onStartTour={() => {}}
      />      {/* ── Main content area ── */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden", marginTop: "calc(74px + var(--banner-height, 0px))" }}>{loadingVehicles && (          <div style={{ position: "absolute", top: 74, left: 0, right: 0, background: "#fff8e1", borderBottom: "1px solid #ffc107", padding: "6px 14px", fontSize: 12, color: "#856404", zIndex: 50 }}>
            Loading vehicles from Athena…
          </div>
        )}
        {vehiclesError && (
          <div style={{ position: "absolute", top: 74, left: 0, right: 0, background: "#fdecea", borderBottom: "1px solid #dc3545", padding: "6px 14px", fontSize: 12, color: "#721c24", zIndex: 50 }}>
            ⚠ Could not load vehicles from backend — showing local data only. ({vehiclesError})
          </div>
        )}
        {activeNav === "home"  && <HomeView />}        {activeNav === "year"  && (          <YearView
            year={year}
            onYearChange={handleYearChange}
            allEvents={allEvents}
            vehicles={vehicles}
            projectColorMap={projectColorMap}
            onSelectVehicle={v => setSelectedVehicleId(v?.id ?? null)}
          />
        )}
        {activeNav === "month" && (          <MonthView
            year={year}
            month={month}
            onMonthChange={handleMonthChange}
            allEvents={allEvents}
            vehicles={vehicles}
            projectColorMap={projectColorMap}
            onSelectVehicle={v => setSelectedVehicleId(v?.id ?? null)}
          />
        )}        {activeNav !== "home" && (          <RightPanel
            vehicles={vehicles}
            selectedVehicle={selectedVehicle}
            projectColorMap={projectColorMap}
            onSelectVehicle={v => setSelectedVehicleId(v?.id ?? null)}
            onGrantHG={handleGrantHG}
          />
        )}
      </div>

      {/* ── Bottom navigation bar ── */}
      <div style={{ background: "white", borderTop: "1px solid #dde3e8", display: "flex", alignItems: "center", padding: "0 14px", height: 52, flexShrink: 0 }}>
        {NAV_ITEMS.map(({ id, label, Icon }) => (
          <div
            key={id}
            onClick={() => setActiveNav(id)}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              padding: "5px 14px", cursor: "pointer", fontSize: 10, fontWeight: 500,
              color: activeNav === id ? "#1e6b6b" : "#888",
              borderBottom: activeNav === id ? "2.5px solid #1e6b6b" : "2.5px solid transparent",
            }}
          >
            <Icon />
            {label}
          </div>
        ))}

        <div style={{ marginLeft: "auto", display: "flex", gap: 4, alignItems: "center" }}>
          <button
            onClick={() => navigate("/")}
            title="Back to Home"
            style={{ background: "none", border: "1px solid #dde3e8", cursor: "pointer", color: "#555", display: "flex", alignItems: "center", padding: "5px 10px", borderRadius: 6, gap: 5, fontSize: 11, fontWeight: 600 }}
          >
            ← Home
          </button>
          <button
            onClick={() => setAddVehicleOpen(true)}            title="Add Vehicle"
            style={{ background: "#1e6b6b", border: "none", cursor: "pointer", color: "white", display: "flex", alignItems: "center", padding: "5px 10px", borderRadius: 6, gap: 5, fontSize: 11, fontWeight: 600 }}
          >
            <IconPlus />
            Vehicle
          </button>
          <button
            onClick={() => setSidebarOpen(true)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#888", display: "flex", alignItems: "center", padding: 4 }}
          >
            <IconMenu />
          </button>
        </div>
      </div>

      {/* ── Overlays ── */}
      {addVehicleOpen && <AddVehicleSidebar onClose={() => setAddVehicleOpen(false)} onSave={handleAddVehicle} />}
      {sidebarOpen    && <SettingsSidebar   onClose={() => setSidebarOpen(false)} />}
    </div>
  );
}

